import { useState } from 'react'
import { SIZE } from 'baseui/button'
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid'
import { Table } from 'baseui/table-semantic'
import { Modal, ModalBody, ModalHeader } from 'baseui/modal'
import { TYPE } from 'baseui/select'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import SDPaginator from '@/shared/components/SDComponents/SDPaginator/SDPaginator'
import SDInput from '@/shared/components/SDComponents/SDInput/SDInput'
import AddEditModal from './AddEditModal'
import useUsersManagement from './useUsersManagement'
import { useSelector } from 'react-redux'
import AddEditModalNonSelectra from './AddEditModal/AddEditModalNonSelectra'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { Alert, Check } from 'baseui/icon'
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox'
import isValidUserZohoId from '@/shared/tools/isValidUserZohoId'
import userTypeLabelKey from './userTypeLabelKey'
import { Card, Header1 } from '@/shared/theme/styled-components'

const UsersManagement = () => {
  const { isSelectraAdmin } = useSelector(selectAuthData)

  const {
    users,
    setSearchValue,
    setPage,
    pageConfigurationIsLoading,
    t,
    isOpen,
    close,
    updateUser,
    changeUserStatus,
    regeneratePassword,
    selectedUser,
    setIsOpen,
    usersError,
    lbdFilter,
    setLbdFilter,
  } = useUsersManagement()

  const [searchInput, setSearchInput] = useState('')

  const flexGridItemProps = {
    display: 'flex',
    alignItems: 'center',
  }

  const COLUMNS = [
    t('usersManagement:table.organization'),
    t('usersManagement:table.name'),
    t('usersManagement:table.email'),
    t('usersManagement:table.user_type'),
    t('usersManagement:table.zoho_id'),
    t('usersManagement:table.zoho_is_active'),
    t('usersManagement:table.lbd'),
    '',
    '',
    '',
    '',
  ]

  const transformUser = users => {
    if (!users) {
      return []
    }
    return users.map(user => {
      const zohoStatusIcon = !isValidUserZohoId(user.zoho_id) ? (
        <Alert color={'orange'} size={32} title={t('usersManagement:zoho_invalid_id')} />
      ) : user.zoho_is_active === 1 ? (
        <Check color={'green'} size={32} title={t('usersManagement:zoho_active')} />
      ) : (
        <Alert color={'red'} size={32} title={t('usersManagement:zoho_not_active')} />
      )

      return [
        user.organisation?.name ?? '',
        user.firstname + ' ' + user.name,
        user.email,
        t(userTypeLabelKey(user)),
        user.zoho_id,
        zohoStatusIcon,
        user.business_developer ? <Check color={'green'} size={32} title={t('usersManagement:table.lbd')} /> : '',
        <SDButton
          id={'user-' + user.id + '-edit-btn'}
          key={user.id}
          size={SIZE.compact}
          onClick={() => updateUser(user)}
        >
          {t('common:edit')}
        </SDButton>,
        <SDButton key={user.id} size={SIZE.compact} onClick={() => changeUserStatus('active', user.id, user.is_active)}>
          {user.is_active === 1 ? t('common:deactivate') : t('usersManagement:activate')}
        </SDButton>,
        <SDButton
          key={user.id}
          size={SIZE.compact}
          onClick={() => {
            if (window.confirm(t('usersManagement:alert'))) {
              changeUserStatus('delete', user.id, user.is_active)
            }
          }}
        >
          {t('common:delete')}
        </SDButton>,
        parseInt(user.organisation?.is_selectra) !== 1 ? (
          <SDButton
            key={user.id}
            size={SIZE.compact}
            onClick={() => regeneratePassword(user.id)}
          >
            {t('usersManagement:modal.regenerate_password')}
          </SDButton>
        ) : (
          ''
        ),
      ]
    })
  }

  if (pageConfigurationIsLoading) {
    return <Card>{t('common:fetching_data')}</Card>
  }

  if (usersError.error) {
    return (
      <Card>
        <Header1>{usersError.error ?? t('usersManagement:error_occurred')}</Header1>
      </Card>
    )
  }

  return (
    <Card>
      <Header1 id='users-title'>{t('usersManagement:title')}</Header1>
      {users.total && (
        <p>
          {users.total} {t('usersManagement:users_found')}
        </p>
      )}
      <FlexGrid flexGridColumnCount={2} marginBottom='.5rem' alignItems='center'>
        <FlexGridItem {...flexGridItemProps}>
          <SDButton key={0} block={false} size={SIZE.compact} onClick={() => updateUser(null)}>
            {t('usersManagement:create_button')}
          </SDButton>
        </FlexGridItem>
        <FlexGridItem {...flexGridItemProps} justifyContent='flex-end'>
          <Checkbox
            checked={lbdFilter}
            onChange={e => setLbdFilter(e.target.checked)}
            labelPlacement={LABEL_PLACEMENT.right}
            overrides={{
              Root: {
                style: {
                  marginRight: '2rem',
                },
              },
            }}
          >
            {t('usersManagement:lbd_only')}
          </Checkbox>
          <SDInput
            id='userSearch'
            type={TYPE.search}
            placeholder={t('usersManagement:search_user')}
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setSearchValue(searchInput)
              }
            }}
            overrides={{
              Root: {
                style: {
                  marginRight: '.5rem',
                },
              },
            }}
          />
          <SDButton size={SIZE.compact} onClick={() => setSearchValue(searchInput)}>
            {t('common:search')}
          </SDButton>
        </FlexGridItem>
      </FlexGrid>
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table columns={COLUMNS} data={transformUser(users.data)} />
      </div>
      {users.data && (
        <SDPaginator
          numPages={users.lastPage ?? 1}
          currentPage={users.currentPage ?? 1}
          onPageChange={({ nextPage }) => {
            if (parseInt(nextPage) <= parseInt(users.lastPage)) {
              setPage(nextPage)
            }
          }}
        />
      )}
      <Modal onClose={close} isOpen={isOpen}>
        <ModalHeader>
          {selectedUser.id === 0 ? t('usersManagement:modal.header_create') : t('usersManagement:modal.header_update')}
        </ModalHeader>
        <ModalBody>
          {isSelectraAdmin ? (
            <AddEditModal setIsOpen={setIsOpen} close={close} />
          ) : (
            <AddEditModalNonSelectra setIsOpen={setIsOpen} close={close} />
          )}
        </ModalBody>
      </Modal>
    </Card>
  )
}

export default UsersManagement
